import type {QuestionModelV3} from '@albert-io/models';
import cx from 'classnames';
import React, {useCallback, useLayoutEffect, useRef} from 'react';
import {DroppableItem} from './BankItems.react';
import {useInterchangeableDuplicateItems} from '../../shared/interchangeableItems';
import {useItemAreaCopy} from '../../shared/itemAreaCopy';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';
import {useMathJaxOverflow} from '../../MultipleChoice/useMathJaxOverflow';
import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent/AnnotatableContent';
import {buildQuestionRegionId} from 'client/components/PracticeView/Annotations/shared/annotationRegions';

interface Props {
  question: QuestionModelV3;
  handleItemClick: (itemId: string) => void;
  handleSlotClick: (slotId: string) => void;
  handleResetItem: (itemId: string) => void;
  /** The slot to flash after a placement when the student prefers reduced motion. */
  highlightedSlotId?: string | null;
}
const DropSlots = ({
  question,
  handleItemClick,
  handleSlotClick,
  handleResetItem,
  highlightedSlotId = null
}: Props) => {
  const useStoreHook = useQuestionStore<'item-bank'>(question);
  // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const dropSlots = useStoreHook((state) => state.dropSlots);
  const interchangeable = useInterchangeableDuplicateItems();
  const areaCopy = useItemAreaCopy();
  const {ref: mathJaxOverflowRef, hasMathJaxOverflow} = useMathJaxOverflow();
  const questionId = question?.getId?.();
  const valueRendererRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerValueRenderer = useCallback((slotId: string, el: HTMLDivElement | null) => {
    if (el) {
      valueRendererRefs.current.set(slotId, el);
    } else {
      valueRendererRefs.current.delete(slotId);
    }
  }, []);

  // Align all valueRenderer sections to the same height
  useLayoutEffect(() => {
    const elements = Array.from(valueRendererRefs.current.values());
    if (elements.length === 0) return;

    const align = () => {
      elements.forEach((el) => (el.style.minHeight = ''));
      let maxHeight = 0;
      elements.forEach((el) => {
        maxHeight = Math.max(maxHeight, el.scrollHeight);
      });
      elements.forEach((el) => {
        el.style.minHeight = `${maxHeight}px`;
      });
    };

    align();

    // Re-align when images inside value renderers finish loading
    const images = elements.flatMap((el) => Array.from(el.querySelectorAll('img')));
    images.forEach((img) => img.addEventListener('load', align));

    return () => {
      images.forEach((img) => img.removeEventListener('load', align));
    };
  });

  return (
    <AnnotatableContent
      regionId={questionId ? buildQuestionRegionId(questionId, 'item-bank') : null}
      className='contents'
    >
      <div ref={mathJaxOverflowRef} role='group' aria-label={areaCopy.answerArea} className='mt-x4'>
        <ul
          role='list'
          className={cx('mcq__options-grid m-x0 p-x0 list-none', {
            'mcq__options-grid--mathjax-overflow': hasMathJaxOverflow
          })}
        >
          {dropSlots.map((slot, index) => (
            <li key={slot.id}>
              <DroppableItem
                slot={slot}
                index={index}
                isHighlighted={slot.id === highlightedSlotId}
                interchangeable={interchangeable}
                useStoreHook={useStoreHook}
                onClick={() => handleSlotClick(slot.id)}
                onItemClick={handleItemClick}
                onResetItem={handleResetItem}
                registerValueRenderer={registerValueRenderer}
              />
            </li>
          ))}
        </ul>
      </div>
    </AnnotatableContent>
  );
};
export default DropSlots;
